import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiError } from '@forbiz/web-kit'

import { paymentsApi } from '../lib/payments.js'
import s from './PaymentResultPage.module.css'

// 결과 상태 아이콘 — 이모지 대신 라인 아이콘. 색은 .icon(경고=앰버 / 진행=그린)에서 물려받는다.
const ICON = {
  warn: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3 21.5 20H2.5L12 3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M12 10v4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="12" cy="17.3" r="0.5" fill="currentColor" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
  card: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M2.5 10h19" stroke="currentColor" strokeWidth="1.7" />
      <path d="M6 14.5h4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
} as const

/**
 * 토스 결제창 리다이렉트 수신 (BIZ-78).
 *
 * success: 토스가 붙여준 paymentKey·orderId(=우리 orderKey)·amount로 서버 confirm을 부른다.
 *   금액 대조·중복 승인 차단은 서버(confirmPayment)가 한다 — 여기선 전달만.
 * fail: 토스가 code·message를 붙여준다. 보여주고 주문 상세로 돌려보낸다(재시도 가능).
 * oid = 우리 내부 주문 id(successUrl 만들 때 우리가 붙임) — 돌아갈 상세 경로용.
 */

export function PaymentSuccessPage() {
  const [sp] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const ran = useRef(false) // StrictMode 이중 실행 방지 — confirm은 한 번만

  const oid = sp.get('oid')
  useEffect(() => {
    if (ran.current) return
    ran.current = true
    const paymentKey = sp.get('paymentKey')
    const orderKey = sp.get('orderId') // 토스의 orderId = 우리 orderKey
    const amount = Number(sp.get('amount'))
    if (!paymentKey || !orderKey || !Number.isFinite(amount)) {
      setError('결제 승인 정보가 올바르지 않습니다.')
      return
    }
    paymentsApi
      .confirm({ paymentKey, orderKey, amount })
      .then(() => {
        navigate(oid ? `/orders/${oid}` : '/orders', { replace: true, state: { paid: true } })
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : '결제 승인에 실패했습니다. 잠시 후 다시 시도해 주세요.')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={s.page}>
      {error ? (
        <>
          <div className={`${s.icon} ${s.iconWarn}`} aria-hidden="true">{ICON.warn}</div>
          <h1 className={s.title}>결제 승인에 실패했어요</h1>
          <p className={s.msg} role="alert">{error}</p>
          <p className={s.msg}>결제창에서 결제가 됐다면 중복 결제되지 않으니 안심하세요.</p>
          {oid ? (
            <Link className={`btn ${s.back}`} to={`/orders/${oid}`}>주문으로 돌아가기</Link>
          ) : (
            <Link className={`btn ${s.back}`} to="/orders">주문 내역으로</Link>
          )}
        </>
      ) : (
        <>
          <div className={s.icon} aria-hidden="true">{ICON.card}</div>
          <h1 className={s.title}>결제 승인 중…</h1>
          <p className={s.msg}>잠시만요, 결제를 확정하고 있어요.</p>
        </>
      )}
    </div>
  )
}

export function PaymentFailPage() {
  const [sp] = useSearchParams()
  const oid = sp.get('oid')
  const message = sp.get('message') || '결제가 완료되지 않았습니다.'

  return (
    <div className={s.page}>
      <div className={`${s.icon} ${s.iconWarn}`} aria-hidden="true">{ICON.warn}</div>
      <h1 className={s.title}>결제하지 못했어요</h1>
      <p className={s.msg} role="alert">{message}</p>
      <p className={s.msg}>주문은 그대로 남아 있어요 — 주문에서 다시 결제할 수 있습니다.</p>
      {oid ? (
        <Link className={`btn ${s.back}`} to={`/orders/${oid}`}>주문으로 돌아가기</Link>
      ) : (
        <Link className={`btn ${s.back}`} to="/orders">주문 내역으로</Link>
      )}
    </div>
  )
}
